import { BOOKING_TRIAL_STATUS } from './booking.model';
import { createAction, createActionGroup, emptyProps, props } from '@ngrx/store';

import {
  BookingSubscriptionsPlan,
  ContingentSubscription,
  SubscriptionPlan,
  SubscriptionsDetails,
  TrialSubscriptionsList,
  ViError,
  VoucherValidationDetails,
} from '@vi-iot/client';

import { BookingDashboardDevice, BookingSubscription } from '@store';

export namespace BookingActions {
  export enum Type {
    RESET_USER_SELECTION = '[Booking] Reset User Selection',
    RESET_INSTALLATION_SELECTION = '[Booking] Reset Installation Selection',
    SET_LOADING_INDICATOR = '[Booking] Set Loading Indicator',
    LOAD_BOOKING_INSTALLATIONS = '[Booking] Load Booking Installations',
    SAVE_BOOKING_INSTALLTIONS = '[Booking] Save Booking Installations',
    TOGGLE_INSTALLATION_SELECTION = '[Booking] Toggle InstallationSelection',
    SELECT_BY_ONLINE_DEVICES_WITH_ACTIVE_TRIAL = '[Booking] Select installations by online devices with active trial',
    TRIGGER_SELECT_INSTALLATIONS = '[Booking] Trigger select installations',
    TRIGGER_DESELECT_INSTALLATIONS = '[Booking] Trigger deselect installations',
    SELECT_INSTALLATIONS = '[Booking] Select installations',
    DESELECT_INSTALLATIONS = '[Booking] Deselect installations',
    SELECT_BOOKING_PLAN = '[Booking] Select Booking Plan',
    SUBSCRIBE_COMMERCIAL_PACKAGE = '[Booking] Subscribe Commercial Package',
    SUBSCRIBE_FLAT_RATE_PACKAGE = '[Booking] Subscribe Flat Rate Package',
    SUBSCRIBE_PACKAGE = '[Booking] Subscribe Package',
    SUBSCRIBE_PACKAGE_SUCCESS = '[Booking] Subscribe Package Success',
    SUBSCRIBE_PACKAGE_ERROR = '[Booking] Subscribe Package Error',
    FETCH_SUBSCRIPTIONS_PLANS = '[Booking] Fetch Subscriptions Plans',
    FETCH_SUBSCRIPTIONS_PLANS_SUCCESS = '[Booking] Fetch Subscriptions Plans Success',
    FETCH_SUBSCRIPTIONS_PLANS_ERROR = '[Booking] Fetch Subscriptions Plans Error',
    GET_VITOGUIDE_SUBSCRIPTIONS = '[Booking] Get Vitoguide Subscriptions',
    GET_VITOGUIDE_SUBSCRIPTIONS_SUCCESS = '[Booking] Get Vitoguide Subscriptions Success',
    GET_VITOGUIDE_SUBSCRIPTIONS_ERROR = '[Booking] Get Vitoguide Subscriptions Error',
    UPDATE_VITOGUIDE_SUBSCRIPTION = '[Booking] Update Vitoguide Subscription',
    UPDATE_VITOGUIDE_SUBSCRIPTION_SUCCESS = '[Booking] Update Vitoguide Subscription Success',
    UPDATE_VITOGUIDE_SUBSCRIPTION_ERROR = '[Booking] Update Vitoguide Subscription Error',
    CANCEL_VITOGUIDE_SUBSCRIPTION = '[Booking] Cancel Vitoguide Subscription',
    CANCEL_VITOGUIDE_SUBSCRIPTION_SUCCESS = '[Booking] Cancel Vitoguide Subscription Success',
    CANCEL_VITOGUIDE_SUBSCRIPTION_ERROR = '[Booking] Cancel Vitoguide Subscription Error',
    CREATE_TRIAL_SUBSCRIPTION = '[Booking] Create Trial Subscription',
    CREATE_TRIAL_SUBSCRIPTION_SUCCESS = '[Booking] Create Trial Subscription Success',
    CREATE_TRIAL_SUBSCRIPTION_ERROR = '[Booking] Create Trial Subscription Error',
    GET_TRIAL_SUBSCRIPTIONS = '[Booking] Get Trial Subscriptions',
    GET_TRIAL_SUBSCRIPTIONS_SUCCESS = '[Booking] Get Trial Subscriptions Success',
    GET_TRIAL_SUBSCRIPTIONS_ERROR = '[Booking] Get Trial Subscriptions Error',
    GET_SUBSCRIPTIONS_DETAILS = '[Booking] Get Subscriptions Details',
    GET_SUBSCRIPTIONS_DETAILS_SUCCESS = '[Booking] Get Subscriptions Details Success',
    GET_SUBSCRIPTIONS_DETAILS_ERROR = '[Booking] Get Subscriptions Details Error',
    RESET_SUBSCRIPTIONS_DETAILS = '[Booking] Reset Subscriptions Details',
    SIMULATE_SUBSCRIPTION = '[Booking] Simulate Vitoguide Subscription',
    SIMULATE_SUBSCRIPTION_SUCCESS = '[Booking] Simulate Vitoguide Subscription Success',
    SIMULATE_SUBSCRIPTION_ERROR = '[Booking] Simulate Vitoguide Subscription Error',

    FLAT_RATE_CANCEL_SUBSCRIPTION = '[Booking] Flat rate cancel subscription attempt',
    FLAT_RATE_CANCEL_SUBSCRIPTION_SUCCESS = '[Booking] Flat rate cancel subscription attempt success',
    FLAT_RATE_CANCEL_SUBSCRIPTION_ERROR = '[Booking] Flat rate cancel subscription attempt error',
    FLAT_RATE_GET_PACKAGE_PRICE_DETAILS = '[Booking] Flat rate get package price details',
    COMMERCIAL_GET_PACKAGE_PRICE_DETAILS = '[Booking] Commercial get package price details',
    GET_CONTINGENT_SUBSCRIPTIONS = '[Booking] Get Contingent Subscriptions',
    GET_CONTINGENT_SUBSCRIPTIONS_SUCCESS = '[Booking] Get Contingent Subscriptions Success',
    GET_CONTINGENT_SUBSCRIPTIONS_ERROR = '[Booking] Get Contingent Subscriptions Error',

    SUBSCRIBE_VOUCHER_PACKAGE = '[Booking] Subscribe Voucher Subscriptions',
    SUBSCRIBE_VOUCHER_PACKAGE_SUCCESS = '[Booking] Subscribe Voucher Subscriptions Success',
    SUBSCRIBE_VOUCHER_PACKAGE_ERROR = '[Booking] Subscribe Voucher Subscriptions Error',
    VALIDATE_VOUCHER_PACKAGE = '[Booking] Validate Voucher Subscriptions',
    VALIDATE_VOUCHER_PACKAGE_SUCCESS = '[Booking] Validate Voucher Subscriptions Success',
    VALIDATE_VOUCHER_PACKAGE_ERROR = '[Booking] Validate Voucher Subscriptions Error',

    RESET_CANCEL_SUBSCRIPTION_STATUS = '[Booking] Reset Cancel Subscription Status',
  }

  export const subscribeCommercialPackage = createAction(Type.SUBSCRIBE_COMMERCIAL_PACKAGE);

  export const subscribeFlatRatePackage = createAction(Type.SUBSCRIBE_FLAT_RATE_PACKAGE);

  export const subscribePackage = createAction(
    Type.SUBSCRIBE_PACKAGE,
    props<{ invoiceNumber?: string }>()
  );

  export const subscribePackageSuccess = createAction(
    Type.SUBSCRIBE_PACKAGE_SUCCESS,
    props<{ invoiceNumber?: string }>()
  );

  export const subscribePackageError = createAction(
    Type.SUBSCRIBE_PACKAGE_ERROR,
    props<{
      error: any;
      invoiceNumber?: string;
    }>()
  );

  export const resetUserSelection = createAction(Type.RESET_USER_SELECTION);

  export const setLoadingIndicator = createAction(Type.SET_LOADING_INDICATOR);

  export const loadBookingInstallations = createActionGroup({
    source: 'BOOKING Load booking installations',
    events: {
      attempt: emptyProps(),
      'snapshot not found error': emptyProps(),
      retry: emptyProps(),
      error: props<{ error: ViError }>(),
      success: props<{
        bookingDevices: BookingDashboardDevice[];
        allDevicesCount: number;
        preselectedSerials?: string[];
      }>(),
    },
  });

  export const toggleInstallationSelection = createAction(
    Type.TOGGLE_INSTALLATION_SELECTION,
    props<{ deviceSerial: string }>()
  );

  export const selectInstallations = createAction(
    Type.SELECT_INSTALLATIONS,
    props<{ deviceSerials: string[] }>()
  );

  export const deselectInstallations = createAction(
    Type.DESELECT_INSTALLATIONS,
    props<{ deviceSerials: string[] }>()
  );

  export const triggerSelectInstallations = createAction(
    Type.TRIGGER_SELECT_INSTALLATIONS,
    props<{ deviceSerials: string[] }>()
  );

  export const triggerDeselectInstallations = createAction(
    Type.TRIGGER_DESELECT_INSTALLATIONS,
    props<{ deviceSerials: string[] }>()
  );

  export const selectByOnlineDevicesWithActiveTrial = createAction(
    Type.SELECT_BY_ONLINE_DEVICES_WITH_ACTIVE_TRIAL
  );

  export const selectBookingPlan = createAction(
    Type.SELECT_BOOKING_PLAN,
    props<{
      plan: SubscriptionPlan;
    }>()
  );

  export const fetchSubscriptionsPlans = createAction(Type.FETCH_SUBSCRIPTIONS_PLANS);

  export const fetchSubscriptionsPlansSuccess = createAction(
    Type.FETCH_SUBSCRIPTIONS_PLANS_SUCCESS,
    props<{
      subscriptionsPlans: BookingSubscriptionsPlan[];
    }>()
  );

  export const fetchSubscriptionsPlansError = createAction(
    Type.FETCH_SUBSCRIPTIONS_PLANS_ERROR,
    props<{
      error: any;
    }>()
  );

  export const getVitoguideSubscriptions = createAction(
    Type.GET_VITOGUIDE_SUBSCRIPTIONS,
    props<{
      companyId: string;
    }>()
  );

  export const getVitoguideSubscriptionsSuccess = createAction(
    Type.GET_VITOGUIDE_SUBSCRIPTIONS_SUCCESS,
    props<{
      subscriptions: BookingSubscription[];
    }>()
  );

  export const getVitoguideSubscriptionsError = createAction(
    Type.GET_VITOGUIDE_SUBSCRIPTIONS_ERROR,
    props<{
      error: any;
      isResetSubcriptions?: boolean;
    }>()
  );

  export const updateVitoguideSubscription = createAction(Type.UPDATE_VITOGUIDE_SUBSCRIPTION);

  export const updateVitoguideSubscriptionSuccess = createAction(
    Type.UPDATE_VITOGUIDE_SUBSCRIPTION_SUCCESS,
    props<{
      boilerSerial: string;
    }>()
  );

  export const updateVitoguideSubscriptionError = createAction(
    Type.UPDATE_VITOGUIDE_SUBSCRIPTION_ERROR,
    props<{
      error: any;
    }>()
  );

  export const cancelVitoguideSubscription = createAction(
    Type.CANCEL_VITOGUIDE_SUBSCRIPTION,
    props<{
      cancelReasonCode: string;
      boilerSerial: string;
      additionalComment: string;
      companyId?: string;
    }>()
  );

  export const cancelVitoguideSubscriptionsSuccess = createAction(
    Type.CANCEL_VITOGUIDE_SUBSCRIPTION_SUCCESS,
    props<{
      boilerSerial: string;
    }>()
  );

  export const cancelVitoguideSubscriptionsError = createAction(
    Type.CANCEL_VITOGUIDE_SUBSCRIPTION_ERROR,
    props<{
      error: any;
    }>()
  );

  export const createTrialSubscription = createAction(Type.CREATE_TRIAL_SUBSCRIPTION);

  export const createTrialSubscriptionSuccess = createAction(
    Type.CREATE_TRIAL_SUBSCRIPTION_SUCCESS,
    props<{
      bookingTrialStatus: BOOKING_TRIAL_STATUS;
      bookingSubscriptions: BookingSubscription[];
    }>()
  );

  export const createTrialSubscriptionError = createAction(
    Type.CREATE_TRIAL_SUBSCRIPTION_ERROR,
    props<{
      error: any;
      bookingTrialStatus: BOOKING_TRIAL_STATUS;
    }>()
  );

  export const getTrialSubscriptions = createAction(Type.GET_TRIAL_SUBSCRIPTIONS);

  export const getTrialSubscriptionsSuccess = createAction(
    Type.GET_TRIAL_SUBSCRIPTIONS_SUCCESS,
    props<{
      trialSubscriptions: TrialSubscriptionsList;
    }>()
  );

  export const getTrialSubscriptionsError = createAction(
    Type.GET_TRIAL_SUBSCRIPTIONS_ERROR,
    props<{
      error: any;
    }>()
  );

  export const validateVoucherPackage = createAction(
    Type.VALIDATE_VOUCHER_PACKAGE,
    props<{
      voucher: string;
    }>()
  );

  export const validateVoucherPackageSuccess = createAction(
    Type.VALIDATE_VOUCHER_PACKAGE_SUCCESS,
    props<{
      voucherValidationDetails: VoucherValidationDetails;
    }>()
  );

  export const validateVoucherPackageError = createAction(
    Type.VALIDATE_VOUCHER_PACKAGE_ERROR,
    props<{
      error: ViError;
    }>()
  );

  export const subscribeVoucherPackage = createAction(
    Type.SUBSCRIBE_VOUCHER_PACKAGE,
    props<{
      voucher: string;
      installationId?: string;
    }>()
  );

  export const subscribeVoucherPackageSuccess = createAction(
    Type.SUBSCRIBE_VOUCHER_PACKAGE_SUCCESS
  );

  export const subscribeVoucherPackageError = createAction(
    Type.SUBSCRIBE_VOUCHER_PACKAGE_ERROR,
    props<{
      error: ViError;
    }>()
  );

  export const getSubscriptionsDetails = createAction(
    Type.GET_SUBSCRIPTIONS_DETAILS,
    props<{
      boilerSerial: string;
      update?: boolean;
    }>()
  );

  export const getSubscriptionsDetailsSuccess = createAction(
    Type.GET_SUBSCRIPTIONS_DETAILS_SUCCESS,
    props<{
      subscriptionsDetails: SubscriptionsDetails;
    }>()
  );

  export const getSubscriptionsDetailsError = createAction(
    Type.GET_SUBSCRIPTIONS_DETAILS_ERROR,
    props<{
      error: any;
    }>()
  );

  export const resetSubscriptionsDetails = createAction(Type.RESET_SUBSCRIPTIONS_DETAILS);

  export const simulateSubscription = createAction(
    Type.SIMULATE_SUBSCRIPTION,
    props<{
      companyId: string;
      boilerSerial: string;
    }>()
  );

  export const simulateSubscriptionSuccess = createAction(Type.SIMULATE_SUBSCRIPTION_SUCCESS);

  export const simulateSubscriptionError = createAction(
    Type.SIMULATE_SUBSCRIPTION_ERROR,
    props<{
      error: any;
    }>()
  );

  export const flatRateCancelSubscription = createAction(
    Type.FLAT_RATE_CANCEL_SUBSCRIPTION,
    props<{ cancelReasonCode: string; additionalComment: string; companyId?: string }>()
  );

  export const flatRateCancelSubscriptionSuccess = createAction(
    Type.FLAT_RATE_CANCEL_SUBSCRIPTION_SUCCESS
  );

  export const flatRateCancelSubscriptionError = createAction(
    Type.FLAT_RATE_CANCEL_SUBSCRIPTION_ERROR
  );

  export const flatRateGetPackagePriceDetails = createAction(
    Type.FLAT_RATE_GET_PACKAGE_PRICE_DETAILS
  );

  //TODO To be refactored after BTPD-12437
  export const commercialGetPackagePriceDetails = createAction(
    Type.COMMERCIAL_GET_PACKAGE_PRICE_DETAILS,
    props<{
      subscriptionPlan: SubscriptionPlan.Pro | SubscriptionPlan.Basic;
    }>()
  );

  export const getContingentSubscriptions = createAction(Type.GET_CONTINGENT_SUBSCRIPTIONS);

  export const getContingentSubscriptionsSuccess = createAction(
    Type.GET_CONTINGENT_SUBSCRIPTIONS_SUCCESS,
    props<{
      contingentSubscriptions: ContingentSubscription[];
    }>()
  );

  export const getContingentSubscriptionsError = createAction(
    Type.GET_CONTINGENT_SUBSCRIPTIONS_ERROR,
    props<{
      error: any;
    }>()
  );

  export const resetCancelSubscriptionStatus = createAction(Type.RESET_CANCEL_SUBSCRIPTION_STATUS);

  export const setBookingPaginationPage = createAction(
    '[BOOKING] Set booking pagination',
    props<{ page: number }>()
  );

  export const setSearchInputValue = createAction(
    '[BOOKING] Set search input value',
    props<{ searchInput: string }>()
  );

  export const clearBookingState = createAction('[BOOKING] Clear booking state');
}
